import { Input, Switch, Tab, Tabs, Checkbox, FormControlLabel, Autocomplete, TextField } from "@mui/material";
import Cookies from "js-cookie";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import Modal from "react-modal";
import { useSearchParams, useParams } from "react-router-dom";
import {
  getUser,
  getUsers,
  getUserSessionId,
  loadBotData,
} from "../../API/API";
import { makeNotif } from "../Toastify";
import "./users.css";

const normalizeConversationHtml = (html) =>
  typeof html === "string"
    ? html.replace(
        /<video\s+id=(["'])([^"']+)\1\s*\/>/gi,
        '<span id="$2" data-video-id="$2"></span>'
      )
    : "";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [selectedUserIndex, setSelectedUserIndex] = useState("");
  const [conversation, setConversation] = useState("");
  const [information, setInformation] = useState({});
  const [serviceLtm, setServiceLtm] = useState([]);
  const [userLtm, setUserLtm] = useState([]);
  const [appUsername, setAppUsername] = useState(Cookies.get("username"));
  const [appPassword, setAppPassword] = useState(Cookies.get("password"));
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [memoryChecked, setMemoryChecked] = useState(false);
  const [stateInfoChecked, setStateInfoChecked] = useState(false);
  const [runtimeChecked, setRuntimeChecked] = useState(false);
  const [costChecked, setCostChecked] = useState(false);
  const [flagsChecked, setFlagsChecked] = useState(true);
  const [recommendationsChecked, setRecommendationsChecked] = useState(false);
  const [sentimentAnalysisChecked, setSentimentAnalysisChecked] = useState(false);
  const [healthDataChecked, setHealthDataChecked] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(true);
  const [usersFromAPI, setUsersFromAPI] = useState(true);
  const [includeUsersWithoutChat, setIncludeUsersWithoutChat] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessionId: sessionIdParam, text: searchTextParam } = useParams();
  const [searchSessionId, setSearchSessionId] = useState("");
  const [activeTab, setActiveTab] = useState(2);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("All Companies");
  const [loading, setLoading] = useState(false);
  const [demographicsExpanded, setDemographicsExpanded] = useState(false);
  const [serviceLtmExpanded, setServiceLtmExpanded] = useState(false);
  const [userLtmExpanded, setUserLtmExpanded] = useState(true);

  // Users list cache to store the fetched users list
  const [usersListCache, setUsersListCache] = useState(new Map());
  const CACHE_TTL = 3600 * 1000; // 1 hour in milliseconds
  const [autoScrollDone, setAutoScrollDone] = useState(false);

  // Pagination states
  const [usersPageNumber, setUsersPageNumber] = useState(0);
  const [hasMoreUsersToLoad, setHasMoreUsersToLoad] = useState(true);
  const [isLoadingMoreUsers, setIsLoadingMoreUsers] = useState(false);
  const USERS_PER_PAGE = 100;

  // Helper function to get cache key for users list
  const getUsersListCacheKey = (options = {}) => {
    // Only cache when search is empty - don't cache search results
    if (options.search && options.search.trim() !== '') {
      return null; // Return null to indicate this shouldn't be cached
    }

    const params = {
      is_from_api: options.is_from_api !== undefined ? options.is_from_api : usersFromAPI,
      include_users_without_chat: options.include_users_without_chat !== undefined ? options.include_users_without_chat : includeUsersWithoutChat,
      limit: options.limit || USERS_PER_PAGE,
      offset: options.offset || 0
    };
    return JSON.stringify(params);
  };

  // Helper function to get cached users list
  const getCachedUsersList = (options = {}) => {
    const cacheKey = getUsersListCacheKey(options);
    if (cacheKey) {
      const cachedData = usersListCache.get(cacheKey);
      if (cachedData && Date.now() - cachedData.timestamp < CACHE_TTL) {
        return cachedData.data;
      }
    }
    return null;
  };

  // Helper function to cache users list
  const cacheUsersList = (options = {}, data) => {
    const cacheKey = getUsersListCacheKey(options);
    if (cacheKey) {
      setUsersListCache(prev => new Map(prev).set(cacheKey, { data, timestamp: Date.now() }));
    }
  };

  // Memoize debug options to avoid recreating object on every render
  const debugOpts = useMemo(
    () => ({
      memory: memoryChecked,
      healthData: healthDataChecked,
      stateInfo: stateInfoChecked,
      runtime: runtimeChecked,
      cost: costChecked,
      flags: flagsChecked,
      recommendations: recommendationsChecked,
      sentimentAnalysis: sentimentAnalysisChecked
    }),
    [memoryChecked, healthDataChecked, stateInfoChecked, runtimeChecked, costChecked, flagsChecked, recommendationsChecked, sentimentAnalysisChecked]
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const modalStyle = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      zIndex: 1000,
    },
  };

  const fetchData = async (source) => {
    if ((appUsername && appPassword) || source === "form") {
      try {
        const data = await loadBotData(appUsername, appPassword);
        if (data.message !== "Incorrect credentials") {
          Cookies.set("username", appUsername);
          Cookies.set("password", appPassword);
          setInvalidCredentials(false);
          setModalIsOpen(false);
          const userIdentification = searchParams.get("userIdentification");
          const userPassword = searchParams.get("userPassword");
          if (userIdentification && userPassword) {
            fetchFromQuery(userIdentification, userPassword);
          }
          // If route includes /users/:sessionId, auto-switch to Session ID tab and fetch
          if (sessionIdParam) {
            setActiveTab(1);
            setSearchSessionId(sessionIdParam);
          }
        } else {
          setInvalidCredentials(true);
          setModalIsOpen(true);
          Cookies.remove("username");
          Cookies.remove("password");
        }
      } catch (error) {
        if (error.response?.status === 401) {
          setModalIsOpen(true);
        }
      }
    }
  };

  const handleSearchButtonClick = async (source) => {
    if (activeTab === 0) {
      if (username === "" || password === "") {
        if (source === "debug switch") return;
        makeNotif("error", "Please enter User Identification and Password");
        return;
      }
      try {
        const res = await getUser(
          username,
          password,
          debugOpts
        );
        setConversation(
          res.conversation
            ?.replace(/Copilot:/g, '<span style="color: green;">Coach:</span>')
            ?.replace(/Member:/g, '<span style="color: blue;">Member:</span>')
            ?.replace(
              /Comment:/g,
              '<span style="color: orange;">Comment:</span>'
            )
        );
        setServiceLtm(res.service_ltm || []);
        setUserLtm(res.user_ltm || []);
        const temp = users.filter(
          (user) => user.username === username && (user.password === password)
        );
        console.log(temp);

        if (temp) {
          setInformation({
            ...res.information,
            username: temp[0]?.username || "",
            password: temp[0]?.password || "",
            session_id: temp[0]?.session_id || "",
            member_id: temp[0]?.member_id || "",
          });
        } else {
          setInformation(res.information);
        }
        setSelectedUserIndex("");
      } catch (error) {
        makeNotif("error", `Error fetching user data`);
        console.log(error.message);
      }
    }
    if (activeTab === 1) {
      if (searchSessionId === "") {
        if (source === "debug switch") return;
        makeNotif("error", "Please enter Session ID");
        return;
      }
      try {
        const res = await getUserSessionId(searchSessionId, debugOpts);
        setConversation(
          res.conversation
            ?.replace(/Copilot:/g, '<span style="color: green;">Coach:</span>')
            ?.replace(/Member:/g, '<span style="color: blue;">Member:</span>')
            ?.replace(
              /Comment:/g,
              '<span style="color: orange;">Comment:</span>'
            )
        );
        setServiceLtm(res.service_ltm || []);
        setUserLtm(res.user_ltm || []);
        const temp = users.filter(
          (user) => user.session_id === searchSessionId
        );
        if (temp) {
          setInformation({
            ...res.information,
            username: temp[0]?.username || "",
            password: temp[0]?.password || "",
            session_id: temp[0]?.session_id || "",
            member_id: temp[0]?.member_id || "",
          });
        } else {
          setInformation(res.information);
        }
        setSelectedUserIndex("");
      } catch (error) {
        makeNotif("error", `Error fetching user data`);
        console.log(error);
      }
    }
  };

  const handleDropdownChange = async (event) => {
    const selectedIndex = event.target.value;
    setSelectedUserIndex(selectedIndex);
    if (selectedIndex !== "") {
      const selectedUser = users[selectedIndex];
      try {
        const res = await getUser(
          selectedUser.username,
          selectedUser.password,
          debugOpts
        );
        setConversation(
          res.conversation
            ?.replace(/Copilot:/g, '<span style="color: green;">Coach:</span>')
            ?.replace(/Member:/g, '<span style="color: blue;">Member:</span>')
            ?.replace(
              /Comment:/g,
              '<span style="color: orange;">Comment:</span>'
            )
        );
        setServiceLtm(res.service_ltm || []);
        setUserLtm(res.user_ltm || []);

        setInformation({
          ...res.information,
          username: users[selectedIndex]?.username || "",
          password: users[selectedIndex]?.password || "",
          session_id: users[selectedIndex]?.session_id || "",
          member_id: users[selectedIndex]?.member_id || "",
        });

        setUsername("");
        setPassword("");
        setSearchSessionId("");
      } catch (error) {
        makeNotif("error", `Error fetching user data`);
        console.log(error);
      }
    }
  };

  // Helper function to update UI with user data
  const updateUserData = (res, userData = null) => {
    let html = res.conversation
      ?.replace(/Copilot:/g, '<span style="color: green;">Coach:</span>')
      ?.replace(/Member:/g, '<span style="color: blue;">Member:</span>')
      ?.replace(/Comment:/g, '<span style="color: orange;">Comment:</span>');

    // If a search text param exists, highlight first match
    if (searchTextParam && typeof html === 'string' && html.length > 0) {
      try {
        const escaped = searchTextParam.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const firstMatchRegex = new RegExp(escaped, 'i');
        html = html.replace(firstMatchRegex, match => `<span id="auto-scroll-target" style="background: yellow;">${match}</span>`);
      } catch (e) {
        // noop
      }
    }

    setConversation(html);
    setServiceLtm(res.service_ltm || []);
    setUserLtm(res.user_ltm || []);

    if (userData) {
      setInformation({
        ...res.information,
        username: userData.username || "",
        password: userData.password || "",
        session_id: userData.session_id || "",
        member_id: userData.member_id || "",
      });
    } else {
      setInformation(res.information);
    }
  };

  // Helper function to refresh user data
  const refreshUserData = async (apiCall, userData = null) => {
    try {
      const res = await apiCall;
      updateUserData(res, userData);
    } catch (error) {
      makeNotif("error", `Error refreshing user data`);
      console.log(error);
    }
  };

  useEffect(() => {
    if (activeTab === 0 && username && password) {
      // Tab 0: Search by User Information
      refreshUserData(
        getUser(username, password, debugOpts),
        users.find(user => user.username === username && user.password === password)
      );
    } else if (activeTab === 1 && searchSessionId) {
      // Tab 1: Search by Session ID
      refreshUserData(
        getUserSessionId(searchSessionId, debugOpts),
        users.find(user => user.session_id === searchSessionId)
      );
    } else if (activeTab === 2 && selectedUser && !selectedUser.isLoading) {
      // Tab 2: Manual Selection
      refreshUserData(
        getUser(selectedUser.username, selectedUser.password, debugOpts),
        selectedUser
      );
    }
  }, [debugOpts, selectedUser, activeTab, username, password, searchSessionId]);

  const handleLogin = async (e) => {
    e.preventDefault();
    await fetchData("form");
  };

  const fetchFromQuery = async (username, password) => {
    try {
      setUsername(username);
      setPassword(password);
      const res = await getUser(
        username,
        password,
        {
          memory: false,
          stateInfo: false,
          runtime: false,
          cost: false,
          flags: false
        }
      );
      setConversation(
        res.conversation
          ?.replace(/Copilot:/g, '<span style="color: green;">Coach:</span>')
          ?.replace(/Member:/g, '<span style="color: blue;">Member:</span>')
          ?.replace(/Comment:/g, '<span style="color: orange;">Comment:</span>')
      );
      setServiceLtm(res.service_ltm || []);
      setUserLtm(res.user_ltm || []);
      setInformation(res.information);
    } catch (error) {
      makeNotif("error", `Error fetching user data`);
      console.log(error);
    }
  };

  const handleClear = (fields) => {
    if (fields === "sessionId") {
      setSearchSessionId("");
      return;
    }
    if (fields === "usernamePassword") {
      setUsername("");
      setPassword("");
    }
  };

  // Initial data fetch on component mount
  useEffect(() => {
    fetchData("useEffect");
  }, []);

  // Helper function to clean expired cache entries
  const cleanExpiredCache = () => {
    const now = Date.now();
    setUsersListCache(prev => {
      const newCache = new Map();
      for (const [key, value] of prev.entries()) {
        if (now - value.timestamp < CACHE_TTL) {
          newCache.set(key, value);
        }
      }
      return newCache;
    });
  };

  // When navigating directly to /users/:sessionId, switch to the Session ID tab
  useEffect(() => {
    if (sessionIdParam) {
      setActiveTab(1);
    }
  }, [sessionIdParam]);

  // After authentication, if we have a :sessionId param, trigger the search programmatically
  useEffect(() => {
    if (sessionIdParam && !modalIsOpen) {
      setSearchSessionId(sessionIdParam);
      refreshUserData(
        getUserSessionId(sessionIdParam, debugOpts),
        users.find(user => user.session_id === sessionIdParam)
      );
    }
  }, [sessionIdParam, modalIsOpen, debugOpts]);

  // After conversation HTML is set, if :text param exists, scroll to first match
  useEffect(() => {
    if (!autoScrollDone && searchTextParam) {
      const el = document.getElementById('auto-scroll-target');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setAutoScrollDone(true);
      }
    }
  }, [conversation, searchTextParam, autoScrollDone]);

  // Smart fetch users - only when credentials are available
  useEffect(() => {
    if (appUsername && appPassword && !modalIsOpen) {
      fetchUsers();
    }
  }, [appUsername, appPassword, modalIsOpen]);

  // Fetch users when filters change (only if we have credentials)

  useEffect(() => {
    const companiesSet = new Set();
    users.forEach((user) => {
      if (user.company_name) {
        companiesSet.add(user.company_name);
      }
    });
    setCompanies(["All Companies", ...Array.from(companiesSet)]);
  }, [users]);



  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
    setSelectedUserIndex("");
    setSelectedUser(null);
  };

  const handleAutocompleteChange = async (event, newValue) => {
    setSelectedUser(newValue);

    if (newValue && !newValue.isLoading) {
      // Find the user index in the original users array
      const userIndex = users.findIndex(user =>
        user.username === newValue.username &&
        user.password === newValue.password
      );

      if (userIndex !== -1) {
        setSelectedUserIndex(userIndex);
        try {
          const res = await getUser(
            newValue.username,
            newValue.password,
            debugOpts
          );
          setConversation(
            res.conversation
              ?.replace(/Copilot:/g, '<span style="color: green;">Coach:</span>')
              ?.replace(/Member:/g, '<span style="color: blue;">Member:</span>')
              ?.replace(
                /Comment:/g,
                '<span style="color: orange;">Comment:</span>'
              )
          );
          setServiceLtm(res.service_ltm || []);
          setUserLtm(res.user_ltm || []);

          setInformation({
            ...res.information,
            username: users[userIndex]?.username || "",
            password: users[userIndex]?.password || "",
            session_id: users[userIndex]?.session_id || "",
            member_id: users[userIndex]?.member_id || "",
          });

          setUsername("");
          setPassword("");
          setSearchSessionId("");
        } catch (error) {
          makeNotif("error", `Error fetching user data`);
          console.log(error);
        }
      }
    } else if (!newValue) {
      setSelectedUserIndex("");
    }
  };

  // Debounced search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Memo-ize options so we don't rebuild on every keystroke
  const userOptions = useMemo(() => {
    const filteredUsers = (users ?? [])
      .filter(
        (user) =>
          user &&
          user.is_from_api === usersFromAPI &&
          (selectedCompany === "All Companies" ||
            user.company_name === selectedCompany)
      )
      .map((user) => ({
        label: `${user.username || ''} - ${user.password || ''}${user.start_time ? ` - ${user.start_time}` : ""
          }`,
        username: user.username || '',
        password: user.password || '',
        ...user
      }));

    // Add loading indicator option if loading more users
    if (isLoadingMoreUsers && hasMoreUsersToLoad) {
      filteredUsers.push({
        label: 'Loading more users...',
        username: '',
        password: '',
        isLoading: true
      });
    }

    return filteredUsers;
  }, [users, usersFromAPI, selectedCompany, isLoadingMoreUsers, hasMoreUsersToLoad, debouncedSearchTerm]);

  // Debounce effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400); // 400ms delay

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Update search term when user types in Autocomplete
  const handleSearchInputChange = useCallback((event, newInputValue) => {
    setSearchTerm(newInputValue);
  }, []);

  // Infinite scroll handler
  const handleScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 5; // 5px threshold

    if (isNearBottom && hasMoreUsersToLoad && !isLoadingMoreUsers && !loading) {
      const nextPage = usersPageNumber + 1;
      fetchUsers(nextPage, true);
    }
  }, [hasMoreUsersToLoad, isLoadingMoreUsers, loading, usersPageNumber]);

  const fetchUsers = async (page = 0, append = false) => {
    try {
      // Clean expired cache entries before checking cache
      cleanExpiredCache();

      // Create options object for this request
      const requestOptions = {
        is_from_api: usersFromAPI,
        limit: USERS_PER_PAGE,
        offset: page * USERS_PER_PAGE,
        search: debouncedSearchTerm.trim() || undefined,
        include_users_without_chat: includeUsersWithoutChat
      };

      // Check if this exact request is cached (only for empty searches)
      const cachedData = getCachedUsersList(requestOptions);
      if (cachedData) {
        if (append) {
          setUsers(prev => [...prev, ...cachedData]);
        } else {
          setUsers(cachedData);
        }
        setHasMoreUsersToLoad(cachedData.length === USERS_PER_PAGE);
        setUsersPageNumber(page);
        return;
      }

      if (page === 0) {
        setLoading(true);
      } else {
        setIsLoadingMoreUsers(true);
      }

      const res = await getUsers(requestOptions);

      // Only cache if search is empty
      cacheUsersList(requestOptions, res);

      if (append) {
        setUsers(prev => [...prev, ...res]);
      } else {
        setUsers(res);
      }

      // Check if there are more users to load
      setHasMoreUsersToLoad(res.length === USERS_PER_PAGE);
      setUsersPageNumber(page);

    } catch (error) {
      console.error("Error getting users: ", error);
    } finally {
      setLoading(false);
      setIsLoadingMoreUsers(false);
    }
  };

  // Fetch users when filters change (only if we have credentials)
  useEffect(() => {
    if (activeTab === 2 && appUsername && appPassword && !modalIsOpen) {
      // Reset pagination for new filter combination
      setUsers([]);
      setUsersPageNumber(0);
      setHasMoreUsersToLoad(true);
      // Don't clear cache - let fetchUsers check if this filter combination is already cached
      fetchUsers(0, false);
    }
  }, [includeUsersWithoutChat, usersFromAPI, activeTab, appUsername, appPassword, modalIsOpen]);

  // Handle search term changes separately without clearing cache
  useEffect(() => {
    if (activeTab === 2 && appUsername && appPassword && !modalIsOpen) {
      // Reset pagination for new search
      setUsers([]);
      setUsersPageNumber(0);
      setHasMoreUsersToLoad(true);
      // Don't clear cache - let fetchUsers check if this search is already cached
      fetchUsers(0, false);
    }
  }, [debouncedSearchTerm]);

  const renderDebugCheckboxes = () => (
    <div className="switch-container">
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={flagsChecked}
              onChange={(e) => setFlagsChecked(e.target.checked)}
            />
          }
          label="Flags"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={runtimeChecked}
              onChange={(e) => setRuntimeChecked(e.target.checked)}
            />
          }
          label="Runtime"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={costChecked}
              onChange={(e) => setCostChecked(e.target.checked)}
            />
          }
          label="Cost"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={stateInfoChecked}
              onChange={(e) => setStateInfoChecked(e.target.checked)}
            />
          }
          label="State Info"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={memoryChecked}
              onChange={(e) => setMemoryChecked(e.target.checked)}
            />
          }
          label="Memory"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={healthDataChecked}
              onChange={(e) => setHealthDataChecked(e.target.checked)}
            />
          }
          label="Health Data"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={recommendationsChecked}
              onChange={(e) => setRecommendationsChecked(e.target.checked)}
            />
          }
          label="Recommendations"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sentimentAnalysisChecked}
              onChange={(e) => setSentimentAnalysisChecked(e.target.checked)}
            />
          }
          label="Sentiment Analysis"
        />
      </div>
    </div>
  );


  // console.log(JSON.parse(information["Plan 1"]["Success Plan"]));

  return (
    <div className="users-container">
      <Modal isOpen={modalIsOpen} style={modalStyle}>
        <form className="login-form" onSubmit={handleLogin}>
          <Input
            type="text"
            name="username"
            placeholder="Username"
            value={appUsername}
            onChange={(e) => setAppUsername(e.target.value)}
          />
          <Input
            type="password"
            name="password"
            placeholder="Password"
            value={appPassword}
            onChange={(e) => setAppPassword(e.target.value)}
          />
          {invalidCredentials && (
            <p style={{ color: "red", margin: 0 }}>Invalid credentials</p>
          )}
          <button className="button">Login</button>
        </form>
      </Modal>
      <Tabs value={activeTab} onChange={handleTabChange} centered>
        <Tab label="Search by User Information" />
        <Tab label="Search by Session ID" />
        <Tab label="Manual Selection" />
      </Tabs>

      {activeTab === 0 && (
        <div className="search-container">
          <div className="input-container">
            <Input
              placeholder="User Identification"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <Input
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {/* {(username !== "" || password !== "") && (
              <button
                className="clear-button button"
                onClick={() => handleClear("usernamePassword")}
              >
                Clear
              </button>
            )} */}
          </div>
          {renderDebugCheckboxes()}
          <button className="button" onClick={handleSearchButtonClick}>
            Search
          </button>
        </div>
      )}

      {activeTab === 1 && (
        <div className="session-id-container">
          <Input
            placeholder="Session ID"
            value={searchSessionId}
            onChange={(e) => setSearchSessionId(e.target.value)}
          />
          {/* {searchSessionId !== "" && (
            <button
              className="clear-button button"
              onClick={() => handleClear("sessionId")}
            >
              Clear
            </button>
          )} */}
          {renderDebugCheckboxes()}
          <button className="button" onClick={handleSearchButtonClick}>
            Search
          </button>
        </div>
      )}

      {activeTab === 2 && (
        <div className="select-container">
          <div className="select-options">
            <select
              className="dropdown"
              value={selectedCompany}
              onChange={handleCompanyChange}
            >
              {companies.map((company, idx) => (
                <option key={idx} value={company}>
                  {company}
                </option>
              ))}
            </select>
            <Autocomplete
              value={selectedUser}
              onChange={handleAutocompleteChange}
              onInputChange={handleSearchInputChange}
              options={userOptions}
              getOptionLabel={(option) => option.label || ''}
              filterOptions={(options) => options}
              loading={loading || isLoadingMoreUsers}
              loadingText={isLoadingMoreUsers ? "Loading more users..." : "Loading users..."}
              noOptionsText="No users found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for session by user's name, email, or IDs"
                  variant="outlined"
                  size="small"
                  className="searchable-dropdown-input"
                />
              )}
              className="searchable-dropdown"
              ListboxProps={{
                onScroll: handleScroll
              }}
            />
            <div className="select-switch">
              <p>Show users from API:</p>
              <Switch
                checked={usersFromAPI}
                onChange={() => setUsersFromAPI((prev) => !prev)}
              />
            </div>
            <div className="select-switch">
              <p>Include users without chat:</p>
              <Switch
                checked={includeUsersWithoutChat}
                onChange={() => setIncludeUsersWithoutChat((prev) => !prev)}
              />
            </div>
          </div>
          {renderDebugCheckboxes()}
        </div>
      )}

      <div className="content">
        <div className="text">
          <h2>Conversation</h2>
          <div
            id="conversation"
            dangerouslySetInnerHTML={{ __html: normalizeConversationHtml(conversation) }}
          ></div>
        </div>
        <div className="text">
          <h2>Memory</h2>
          <div className="expander-container">
            <button
              className="expander-header"
              onClick={() => setDemographicsExpanded(!demographicsExpanded)}
            >
              <span>Demographics</span>
              <span className="expand-icon">
                {demographicsExpanded ? '▼' : '▶'}
              </span>
            </button>
            {demographicsExpanded && (
              <div>
                <ul>
                  <li>
                    <strong>Username:</strong> {information?.username}
                  </li>
                  <li>
                    <strong>Password:</strong> {information?.password}
                  </li>
                  <li>
                    <strong>Session ID:</strong> {information?.session_id}
                  </li>
                  <li>
                    <strong>Member ID:</strong> {information?.member_id}
                  </li>
                  {Object.entries(information).map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong>
                      {typeof value === "object" && !Array.isArray(value)
                        ? Object.entries(value).map(([subKey, subValue]) => (
                          <div key={subKey}>
                            <strong>{subKey}:</strong> {JSON.stringify(subValue)}
                          </div>
                        ))
                        : Array.isArray(value)
                          ? value.map((item, index) => (
                            <div key={index}>
                              {typeof item === "object"
                                ? JSON.stringify(item)
                                : item}
                            </div>
                          ))
                          : value}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="expander-container ltm-expander">
            <button
              className="expander-header"
              onClick={() => setServiceLtmExpanded(!serviceLtmExpanded)}
            >
              <span>Service Info</span>
              <span className="expand-icon">
                {serviceLtmExpanded ? '▼' : '▶'}
              </span>
            </button>
            {serviceLtmExpanded && (
              <div>
                {serviceLtm && serviceLtm.length > 0 ? (
                  <div className="ltm-list">
                    {serviceLtm.map((item, index) => (
                      <div key={index} className="ltm-entry">
                        <div className="ltm-field">
                          {item.type && (
                            <>
                              <strong>Type:</strong> <span className="ltm-tag">{item.type}</span>
                            </>
                          )}
                          {item.sub_type && (
                            <>
                              <strong>Sub Type:</strong> <span className="ltm-tag">{item.sub_type}</span>
                            </>
                          )}
                        </div>
                        {item.content && (
                          <div className="ltm-field">
                            <strong>Content:</strong> {item.content}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ltm-list">
                    <div className="ltm-entry">
                      <div className="ltm-field">
                        <p>No service LTM data available</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="expander-container ltm-expander">
            <button
              className="expander-header"
              onClick={() => setUserLtmExpanded(!userLtmExpanded)}
            >
              <span>User Long Term Memory</span>
              <span className="expand-icon">
                {userLtmExpanded ? '▼' : '▶'}
              </span>
            </button>
            {userLtmExpanded && (
              <div>
                {userLtm && userLtm.length > 0 ? (
                  <div className="ltm-list">
                    {userLtm.map((item, index) => (
                      <div key={index} className="ltm-entry">
                        <div className="ltm-field">
                          {item.type && (
                            <>
                              <strong>Type:</strong> <span className="ltm-tag">{item.type}</span>
                            </>
                          )}
                          {item.sub_type && (
                            <>
                              <strong>Sub Type:</strong> <span className="ltm-tag">{item.sub_type}</span>
                            </>
                          )}
                        </div>
                        {item.content && (
                          <div className="ltm-field">
                            <strong>Content:</strong> {item.content}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ltm-list">
                    <div className="ltm-entry">
                      <div className="ltm-field">
                        <p>No user LTM data available</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
